import { ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit, ViewChild, effect, inject } from '@angular/core';
import { EFLogicService } from './environmental-fate.logic.service';
import { EFCakeTopologyComponent } from '../ef-cake-topology/ef-cake-topology/ef-cake-topology.component';
import { EFSetupComponent } from '../ef-setup/ef-setup/ef-setup.component';
import { EFDataComponent } from '../ef-data/ef-data/ef-data.component';
import { EFRunComponent } from '../ef-run/ef-run/ef-run.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SelectedProjectApiService } from 'src/app/shared/components/selected-project/selected-project.api.service';
import { Message } from 'primeng/api';
import { Project } from 'src/app/shared/models/project';
import { CustomTabMenuItem } from 'src/app/shared/models/custom-tab-menu-item';
import { Constants } from 'src/app/shared/utils/constants';
import { Environmental_Assessments, UserLogicService } from 'src/app/shared/services/user.logic.service';
import { ModelLibrary, ModelLibraryCollection } from 'src/app/shared/models/model-library';
import { firstValueFrom, take } from 'rxjs';
import { ModelLibraryApiService } from 'src/app/shared/services/model-library.api.service';
import { OrderByPipe } from 'src/app/shared/pipes/order-by.pipe';
import { EFSetupColDef } from '../ef-setup/ef-setup/ef-setup-coldef';
import { DisciplineApiService } from 'src/app/shared/services/echo/discipline.api.service';
import { Discipline } from 'src/app/shared/models/echo/discipline';
import { SetupLogicService } from 'src/app/shared/services/setup.logic.service';

@Component({
  selector: 'app-environmental-fate',
  templateUrl: './environmental-fate.component.html',
  styleUrls: ['./environmental-fate.component.css']
})
export class EnvironmentalFateComponent implements OnInit, OnDestroy {
  activeItem?: CustomTabMenuItem;
  message!: Message;
  loading: boolean = false;
  nextIndex?: number;
  selectedProject?: Project;
  selectedProjectStatus?: number;
  isSetupValid: boolean = false;
  destroyRef = inject(DestroyRef);
  isProjectOwnershipValid: boolean = true;
  environmentalAssessment: Environmental_Assessments = Environmental_Assessments.EF;
  columnDef: any[] = [];
  disciplinePk: number = 0;
  modelsCollection: ModelLibraryCollection[] = [];
  cakeModels: ModelLibrary[] = [];
  featureAcronym = Environmental_Assessments.EF;

  @ViewChild(EFCakeTopologyComponent) efCakeTopologyComponent?: EFCakeTopologyComponent;
  @ViewChild(EFSetupComponent) efSetupComponent?: EFSetupComponent;
  @ViewChild(EFDataComponent) efDataComponent?: EFDataComponent;
  @ViewChild(EFRunComponent) efRunComponent?: EFRunComponent;

  private setupLogicService = inject(SetupLogicService);
  private efSetupColDef = inject(EFSetupColDef);

  constructor(
    public EFLogicService: EFLogicService,
    private selectedProjectApiService: SelectedProjectApiService,
    private userLogicService: UserLogicService,
    private orderByPipe: OrderByPipe,
    private modelLibraryApiService: ModelLibraryApiService,
    private disciplineApiService: DisciplineApiService,
    private cdr: ChangeDetectorRef
  ) {}

  selectedGeographyEffect = effect(() => {
    if (this.setupLogicService.selectedGeographyKey()) {
      this.initColumnDef();
    }
  });

  async ngOnInit(): Promise<void> {
    this.loading = true;
    await this.getDiscipline();
    await this.initModels();
    this.initColumnDef();
    this.initSubscribes();
    const firstItem = this.EFLogicService.items.find((i: CustomTabMenuItem) => i.index === 0);
    if (firstItem) {
      this.EFLogicService.setActiveItem(firstItem);
    }
  }

  async getDiscipline(): Promise<void> {
    await firstValueFrom(this.disciplineApiService.getDisciplineByName(Constants.DISCIPLINE_IDENTITIES.ENVIRONMENTAL_E_FATE).pipe(take(1))).then((discipline: Discipline) => {
      this.disciplinePk = discipline?.disciplinePk ?? -1;
    });
  }

  async initModels() {
    await firstValueFrom(this.modelLibraryApiService.getModels()).then((library: ModelLibrary[]) => {
      const models = library.filter(f => 
        f.disciplineXModel?.some(d => d == this.disciplinePk) &&
        f.nameVersion?.toLowerCase().includes('cake')
      );
      this.cakeModels = this.orderByPipe.transform(models, 'nameVersion');
      
      this.modelsCollection = [{ name: 'cake', library: this.cakeModels }];
    });
  }

  initColumnDef() {
    const selectedGeography = this.setupLogicService.selectedGeographyKey();
    const geographyFilteredCakeModels = selectedGeography
      ? this.cakeModels.filter(model =>
          model.geographyXModel?.some(geography => this.setupLogicService.isSelectedGeography(geography, selectedGeography))
        )
      : this.cakeModels;

    this.columnDef = this.efSetupColDef.getColumnsDefinition(geographyFilteredCakeModels, this.isProjectOwnershipValid);
  }

  async onActiveIndexChange(index: number) {
    if (index !== 3) {
      this.EFLogicService.updateIsLoading(true);
    }

    const previousIndex = this.activeItem?.index;
    const leavingTopology = previousIndex === 1 && index !== 1;
    const enteringTopology = previousIndex !== 1 && index === 1;
    const enteringData = previousIndex !== 2 && index === 2;
    const leavingData = previousIndex === 2 && index !== 2;

    if (leavingTopology) {
      let efateSetupPk = this.efSetupComponent ? await this.efSetupComponent.getCurrentEfateSetupPk() : undefined;
      if (!efateSetupPk) {
        const setupSaved = this.efSetupComponent ? await this.efSetupComponent.saveEfateSetup() : true;
        if (setupSaved) {
          efateSetupPk = this.efSetupComponent ? await this.efSetupComponent.getCurrentEfateSetupPk() : undefined;
        }
      }

      if (efateSetupPk) {
        await this.efCakeTopologyComponent?.saveCakeTopologyPayload(efateSetupPk);
      }
    }

    if (leavingData) {
      const efateSetupPk = this.efSetupComponent ? await this.efSetupComponent.getCurrentEfateSetupPk() : undefined;
      if (efateSetupPk) {
        await this.efDataComponent?.saveDataSets(efateSetupPk);
      }
    }

    if (enteringTopology) {
      this.efCakeTopologyComponent?.selectFirstCakeGroup();

      const currentSetup = this.efSetupComponent?.getCurrentSetupSelection();
      if (currentSetup?.moleculePk && currentSetup?.geography) {
        const setupSaved = this.efSetupComponent ? await this.efSetupComponent.saveEfateSetup() : true;
        if (setupSaved) {
          const efateSetupPk = this.efSetupComponent ? await this.efSetupComponent.getCurrentEfateSetupPk() : undefined;
          if (efateSetupPk) {
            await this.efCakeTopologyComponent?.loadCakeTopologyPayloadBySetup(efateSetupPk);
            this.efCakeTopologyComponent?.selectFirstCakeGroup();
          }
        }
      }
    }

    if (enteringData) {
      let efateSetupPk = this.efSetupComponent ? await this.efSetupComponent.getCurrentEfateSetupPk() : undefined;
      if (!efateSetupPk) {
        const currentSetup = this.efSetupComponent?.getCurrentSetupSelection();
        if (currentSetup?.moleculePk && currentSetup?.geography) {
          const setupSaved = this.efSetupComponent ? await this.efSetupComponent.saveEfateSetup() : true;
          if (setupSaved) {
            efateSetupPk = this.efSetupComponent ? await this.efSetupComponent.getCurrentEfateSetupPk() : undefined;
          }
        }
      }

      await this.efDataComponent?.loadReadOnlyCakeTopology(efateSetupPk);
    }

    this.nextIndex = index;
    this.EFLogicService.setActiveIndexChanged(true, index);
  }

  initSubscribes(): void {
    this.EFLogicService.activeItem.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((activeItem: CustomTabMenuItem) => {
      this.activeItem = activeItem;
    });

    this.selectedProjectApiService.selectedProject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((project?: Project) => {
      this.selectedProject = project;
      this.isProjectOwnershipValid = this.selectedProject ? this.userLogicService.verifyProjectOwnership(this.selectedProject?.createdBy) : true;
      if (this.selectedProject) {
        const firstItem = this.EFLogicService.items.find((i: CustomTabMenuItem) => i.index === 0);
        if (firstItem) {
          this.EFLogicService.setActiveItem(firstItem);
        }
      }
      this.onActiveIndexChange(0);
    });

    this.selectedProjectApiService.selectedProjectStatus.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status?: number) => {
      this.selectedProjectStatus = status;
    });

    this.EFLogicService.isLoading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isLoading: boolean) => {
      this.loading = isLoading;
    });

    this.EFLogicService.success.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (success: boolean) => {
      this.loading = false;

      if (success) {
        const nextItem = this.EFLogicService.items.find((i: CustomTabMenuItem) => i.index == this.nextIndex);
        if (nextItem) {
          this.EFLogicService.setActiveItem(nextItem);

          if (nextItem.index === 3) {
            const efateSetupPk = this.efSetupComponent ? await this.efSetupComponent.getCurrentEfateSetupPk() : undefined;
            await this.efRunComponent?.runCake(efateSetupPk);
          }
        }
      }
    });
  }

  validateSetup(event: boolean) {
    this.isSetupValid = event;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.selectedGeographyEffect.destroy();
  }
}
